import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import { openBookDemo } from '../utils/config';

const Header = ({ onBlogClick, onCaseStudiesClick, onResourcesClick, onROICalculatorClick, onShopClick, onMethodologyClick, onAboutClick }) => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const currentLanguage = i18n.language;

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 100) {
        // Always show header near the top
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down - hide header
        setIsVisible(false);
      } else {
        // Scrolling up - show header
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const navLinks = [
    { name: t('nav.aboutUs', 'About Us'), href: 'aboutUs' },
    { name: t('nav.methodology', '5D Model'), href: 'methodology' },
    { name: t('nav.services', 'Services'), href: 'shop' },
    { name: t('nav.caseStudies'), href: 'caseStudies' },
    { name: t('nav.resources'), href: 'resources' },
    { name: t('nav.blog'), href: 'blog' }
  ];

  const scrollToSection = (href) => {
    if (href === 'blog') {
      onBlogClick && onBlogClick();
      setIsMobileMenuOpen(false);
      return;
    }
    if (href === 'caseStudies') {
      onCaseStudiesClick && onCaseStudiesClick();
      setIsMobileMenuOpen(false);
      return;
    }
    if (href === 'resources') {
      onResourcesClick && onResourcesClick();
      setIsMobileMenuOpen(false);
      return;
    }
    if (href === 'shop') {
      onShopClick && onShopClick();
      setIsMobileMenuOpen(false);
      return;
    }
    if (href === 'methodology') {
      onMethodologyClick && onMethodologyClick();
      setIsMobileMenuOpen(false);
      return;
    }
    if (href === 'aboutUs') {
      onAboutClick && onAboutClick();
      setIsMobileMenuOpen(false);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 bg-white shadow-md ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-[7.5rem]">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src="/coachmeupp-logo.png"
              alt="CoachMeUpp Logo"
              className="h-24 md:h-32 w-auto cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              data-testid="header-logo"
            />
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href === 'blog' ? '#' : link.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(link.href);
                }}
                className="text-sm font-medium text-gray-700 hover:text-blue-800 transition-colors duration-300"
              >
                {link.name}
              </a>
            ))}
          </nav>
          
          {/* Desktop CTA Buttons & Language Switcher */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Switcher */}
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => changeLanguage('en')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  currentLanguage === 'en' || currentLanguage === 'en-US'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="lang-switch-en"
              >
                EN
              </button>
              <button
                onClick={() => changeLanguage('tr')}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  currentLanguage === 'tr'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="lang-switch-tr"
              >
                TR
              </button>
            </div>
            
            <Button 
              variant="ghost"
              onClick={() => scrollToSection('#contact')}
              className="text-gray-700 hover:text-blue-800"
            >
              {t('nav.signIn')}
            </Button>
            <Button 
              onClick={openBookDemo}
              className="bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="header-book-demo-btn"
            >
              {t('nav.bookDemo')}
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-blue-800"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-xl">
          <nav className="container mx-auto px-6 py-6 flex flex-col gap-4">
            {/* Mobile Language Switcher */}
            <div className="flex items-center justify-center gap-2 pb-4 border-b border-gray-200">
              <button
                onClick={() => changeLanguage('en')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentLanguage === 'en' || currentLanguage === 'en-US'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => changeLanguage('tr')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentLanguage === 'tr'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                TR
              </button>
            </div>
            
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href === 'blog' ? '#' : link.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(link.href);
                }}
                className="text-base font-medium text-gray-700 hover:text-blue-800 transition-colors py-2"
              >
                {link.name}
              </a>
            ))}
            <div className="pt-4 border-t border-gray-200 flex flex-col gap-3">
              <Button 
                variant="outline"
                onClick={() => scrollToSection('#contact')}
                className="w-full"
              >
                {t('nav.signIn')}
              </Button>
              <Button 
                onClick={() => { openBookDemo(); setIsMobileMenuOpen(false); }}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                {t('nav.bookDemo')}
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
